/**
 * The invoice page's dispute panel (docs/features/v1-completion/L_credit_control_spec.md §4.6; mock-up
 * L_credit_control.html state 9): "Disputed since 9 Oct", what the customer says, who raised it, and Resolve dispute
 * for finance. Shown only while the invoice has an open dispute; the Resolve button comes from the dispute's own
 * `resolve_dispute_url`, so a role that cannot resolve sees the panel without it.
 *
 * A dispute is display only in v1: it does not stop Xero's reminders, so the panel says so and points at the
 * credit note that already lives on this page.
 */
import type { AgedDebtDispute } from "@shared-core/data/aged-debt-types";
import { formatShortDateOrDash } from "@shared-core/domain/formatters";
import { Button } from "../../primitives/button";

export function InvoiceDisputePanel({
  dispute,
  onResolve,
}: {
  dispute: AgedDebtDispute;
  onResolve: () => void;
}) {
  return (
    <section
      aria-label="Dispute"
      className="space-y-2 rounded-lg border border-amber-600 bg-card p-4 text-sm"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-medium">
          Disputed since {formatShortDateOrDash(dispute.raised_at)}
        </h2>
        {dispute.resolve_dispute_url && (
          <Button type="button" size="sm" variant="outline" onClick={onResolve}>
            Resolve dispute…
          </Button>
        )}
      </div>
      <p>
        {dispute.reason}{" "}
        {dispute.raised_by_name && (
          <span className="text-xs text-muted-foreground">
            Raised by {dispute.raised_by_name}. Internal: not on the customer's
            copy.
          </span>
        )}
      </p>
      <p className="text-xs text-muted-foreground">
        Chasing is paused here only: Xero still sends its own reminders. If the
        customer is right, raise a credit note and name it when you resolve
        this.
      </p>
    </section>
  );
}
